<template>
  <div class="signature-pad-container">
    <label class="signature-label">
      {{ label }}
      <span v-if="!required" class="optional-tag">(Optional)</span>
    </label>

    <div class="signature-canvas-wrapper" :class="{ 'has-signature': hasSignature }">
      <canvas
        ref="canvas"
        class="signature-canvas"
        @mousedown="onDrawStart"
        @touchstart="onDrawStart"
      ></canvas>

      <div v-if="!hasSignature" class="signature-placeholder">
        Sign here
      </div>
    </div>

    <div class="signature-actions">
      <button
        type="button"
        class="action-btn"
        :disabled="!hasSignature"
        @click="clear"
      >
        <font-awesome-icon icon="eraser" />
        Clear
      </button>
      <button
        type="button"
        class="action-btn"
        :disabled="!canUndo"
        @click="undo"
      >
        <font-awesome-icon icon="undo" />
        Undo
      </button>
    </div>
  </div>
</template>

<script>
import SignaturePad from 'signature_pad';

export default {
  name: 'SignaturePad',
  props: {
    label: {
      type: String,
      default: 'Your Signature',
    },
    required: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: null,
    },
    // Ink color for the signature itself (the rest of the theme is driven by the
    // --tou-sig-* CSS custom properties — see the <style> block).
    penColor: {
      type: String,
      default: '#1e293b',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      signaturePad: null,
      strokeHistory: [],
    };
  },
  computed: {
    hasSignature() {
      return this.signaturePad && !this.signaturePad.isEmpty();
    },
    canUndo() {
      return this.strokeHistory.length > 0;
    },
  },
  mounted() {
    this.initSignaturePad();
    window.addEventListener('resize', this.resizeCanvas);

    // Restore existing signature if provided
    if (this.modelValue) {
      this.restoreSignature(this.modelValue);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resizeCanvas);
    if (this.signaturePad) {
      this.signaturePad.off();
    }
  },
  methods: {
    initSignaturePad() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      // Set canvas dimensions
      this.resizeCanvas();

      // Initialize signature pad
      this.signaturePad = new SignaturePad(canvas, {
        backgroundColor: 'rgba(255, 255, 255, 0)',
        penColor: this.penColor,
        minWidth: 1,
        maxWidth: 3,
        throttle: 16,
      });

      // Listen for stroke end to save history and emit updates
      this.signaturePad.addEventListener('endStroke', () => {
        this.saveToHistory();
        this.emitSignature();
      });
    },
    resizeCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      const wrapper = canvas.parentElement;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);

      canvas.width = wrapper.offsetWidth * ratio;
      canvas.height = wrapper.offsetHeight * ratio;
      canvas.style.width = `${wrapper.offsetWidth}px`;
      canvas.style.height = `${wrapper.offsetHeight}px`;

      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);

      // Restore signature after resize if we have data
      if (this.signaturePad && !this.signaturePad.isEmpty()) {
        const data = this.signaturePad.toData();
        this.signaturePad.clear();
        this.signaturePad.fromData(data);
      }
    },
    onDrawStart() {
      // Just ensures the canvas is focused for drawing
    },
    saveToHistory() {
      if (this.signaturePad) {
        this.strokeHistory.push(JSON.stringify(this.signaturePad.toData()));
      }
    },
    clear() {
      if (this.signaturePad) {
        this.signaturePad.clear();
        this.strokeHistory = [];
        this.emitSignature();
      }
    },
    undo() {
      if (this.strokeHistory.length > 0) {
        this.strokeHistory.pop();

        if (this.strokeHistory.length > 0) {
          const previousData = JSON.parse(this.strokeHistory[this.strokeHistory.length - 1]);
          this.signaturePad.fromData(previousData);
        } else {
          this.signaturePad.clear();
        }

        this.emitSignature();
      }
    },
    emitSignature() {
      if (this.signaturePad && !this.signaturePad.isEmpty()) {
        const dataUrl = this.signaturePad.toDataURL('image/png');
        this.$emit('update:modelValue', dataUrl);
      } else {
        this.$emit('update:modelValue', null);
      }
    },
    restoreSignature(dataUrl) {
      if (this.signaturePad && dataUrl) {
        // Load image and draw to canvas
        const img = new Image();
        img.onload = () => {
          const canvas = this.$refs.canvas;
          const ctx = canvas.getContext('2d');
          const ratio = Math.max(window.devicePixelRatio || 1, 1);

          // Clear and draw the image
          this.signaturePad.clear();
          ctx.drawImage(img, 0, 0, canvas.width / ratio, canvas.height / ratio);
        };
        img.src = dataUrl;
      }
    },
    getSignatureData() {
      if (this.signaturePad && !this.signaturePad.isEmpty()) {
        return this.signaturePad.toDataURL('image/png');
      }
      return null;
    },
    isEmpty() {
      return this.signaturePad ? this.signaturePad.isEmpty() : true;
    },
  },
};
</script>

<style scoped>
/*
 * Theme via CSS custom properties so each app can re-skin without forking the
 * component. Defaults below are the congress slate palette; the letters app
 * overrides --tou-sig-* (warm brown/serif) on a parent element.
 */
.signature-pad-container {
  margin: 1.5rem 0;
}

.signature-label {
  display: block;
  font-weight: 500;
  color: var(--tou-sig-label-color, #1e293b);
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.optional-tag {
  font-weight: 400;
  color: var(--tou-sig-muted-color, #64748b);
  font-size: 0.85rem;
}

.signature-canvas-wrapper {
  position: relative;
  width: 100%;
  height: 150px;
  border: 2px dashed var(--tou-sig-border-color, #cbd5e1);
  border-radius: 8px;
  background: var(--tou-sig-bg, #fafafa);
  cursor: crosshair;
  transition: all 0.2s;
}

.signature-canvas-wrapper:hover {
  border-color: var(--tou-sig-border-hover, #94a3b8);
}

.signature-canvas-wrapper.has-signature {
  border-style: solid;
  border-color: var(--tou-sig-accent, #3b82f6);
  background: var(--tou-sig-active-bg, white);
}

.signature-canvas {
  width: 100%;
  height: 100%;
  touch-action: none;
}

.signature-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--tou-sig-placeholder-color, #94a3b8);
  font-size: 1rem;
  font-style: var(--tou-sig-placeholder-font-style, normal);
  pointer-events: none;
  user-select: none;
}

.signature-canvas-wrapper.has-signature .signature-placeholder {
  display: none;
}

.signature-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--tou-sig-btn-border, #e2e8f0);
  border-radius: 6px;
  background: var(--tou-sig-btn-bg, white);
  color: var(--tou-sig-muted-color, #64748b);
  font-family: var(--tou-sig-btn-font-family, inherit);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover:not(:disabled) {
  border-color: var(--tou-sig-accent, #3b82f6);
  color: var(--tou-sig-accent, #3b82f6);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .signature-canvas-wrapper {
    height: 120px;
  }
}
</style>
